import { useRouter } from 'next/router';
import { Flags } from '@watershed/constants/flags';

import type { NextRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { z } from 'zod/v3';
import { Analytics } from '@watershed/analytics/analyticsUtils';
import LoadingPage from '@watershed/shared-frontend/components/LoadingPage';
import assertNever from '@watershed/shared-util/src/assertNever';
import useLocationHash from '@watershed/shared-frontend/hooks/useLocationHash';
import { getLearningHubSlugWithRedirects } from '@watershed/shared-universal/learningHubRoutes';
import { useFeatureFlag } from '../utils/FeatureFlag';

const helpjuiceMessageSchema = z.union([
  z.object({
    messageType: z.literal('search'),
    data: z.object({ query: z.string() }),
  }),
  z.object({
    messageType: z.literal('slug'),
    data: z.object({ slug: z.string() }),
  }),
  z.object({
    messageType: z.literal('onLoad'),
  }),
]);

// Helper function to ignore errors that happen when a new navigation starts before the previous one
// finished. We could wait for the `router.ready == true` before each navigation, but this is a simpler solution
// given how uncommon this edge case is
const replaceRoute = (
  router: NextRouter,
  newRoute: string,
  method: 'replace' | 'push'
) => {
  const routerUpdate = method === 'push' ? router.push : router.replace;
  void routerUpdate(newRoute, undefined, { shallow: true }).catch((e: any) => {
    if (!e.cancelled) {
      throw e;
    }
  });
};

/** Converts a Helpjuice url into a Watershed Url (see above comment) */
export const getWatershedLearnUrl = (url: string | null, baseRoute: string) => {
  if (
    typeof url === 'string' &&
    url.startsWith('https://watershed.helpjuice.com/')
  ) {
    const slug = url.split('https://watershed.helpjuice.com/')[1];
    return `${baseRoute}#${slug}`;
  }
  return null;
};

// Secure URL which helpjuice can only be accessed through
// This helps us prevent helpjuice access outside of watershed. Users can still hypothetically inspect this and share it
// outside of Watershed to access the learning hub, but this at least limits visibility to folks logged into watershed.
const HELPJUICE_ENCRYPTED_URL =
  'https://watershed.helpjuice.com/e/VTJGc2RHVmtYMStUdEdSNEp3ODlJaHhvMGlmRlpYQXR1N0cyREF6R1BhMkFFc0xXWUlhUFcyWmlsRHJYClNvS3RwSVFzZU1OMlJCdzVlWnVKeXpUTEhhWVYvaHhOcHdQQVRqQlB6RzhxeW5ZPQo=';

export default function LearningHubEmbed({ baseRoute }: { baseRoute: string }) {
  // NOTE (bryan, LRN-181): To allow customers to open URLs in a new tab, we
  // need to modify the iframe's contents a bit, to replace
  // `www.watershed.helpjuice.com` URLs with our own URLs (that then redirect to
  // this component & display the content).
  const shouldModifyUrls = useFeatureFlag(Flags.LearningHubReplaceUrls);

  // We recompute the srcUrl of the iframe update the srcUrl of the iframe whenever the hash changes.
  // this let's `srcUrl` be a reliable source of truth on what url is being displayed at any moment.
  const router = useRouter();
  const hash = useLocationHash();

  // If an entry point param is provided, log it and remove it from the URL
  const entryPoint = router.query['from'];
  if (entryPoint) {
    Analytics.action('learningHubAccessedFromProduct', {
      entryPoint,
    });
    replaceRoute(router, `${router.pathname}#${hash}`, 'replace');
  }

  // Removes the leading slash from the hash if exists
  const cleanHash = hash
    ? hash.startsWith('/')
      ? hash.substring(1)
      : hash
    : '';

  // Lets us support articles with anchor links
  // /learn#article-name#anchor-link becomes /learn#article-name%23anchor-link which we convert to `/article-name#anchor-link` for helpjuice
  const hashWithAnchorLink = cleanHash.replace(/%23/g, '#');

  // Check to see if we've changed the link for this slug and replace the url if so
  const hashWithRedirects = getLearningHubSlugWithRedirects(hashWithAnchorLink);
  if (hashWithRedirects !== hashWithAnchorLink) {
    replaceRoute(router, `${router.pathname}#${hashWithRedirects}`, 'replace');
  }
  const srcUrl = `https://watershed.helpjuice.com/${hashWithRedirects}`;

  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Listen for messages from Helpjuice iframe
  useEffect(() => {
    const handler = (e: MessageEvent<string>) => {
      // Ignore all messages that don't come from helpjuice
      if (e.origin !== 'https://watershed.helpjuice.com') return;
      let parsedData;
      try {
        parsedData = helpjuiceMessageSchema.safeParse(JSON.parse(e.data));
        if (parsedData.success === false) return;
      } catch (e) {
        return;
      }
      const { data } = parsedData;

      switch (data.messageType) {
        case 'slug':
          const slug = data.data.slug;
          Analytics.action('learningHubClick', {
            slug,
          });

          // Update the hash in the URL to match the new URL received from Helpjuice
          const newRoute = slug ? `${baseRoute}#${slug}` : baseRoute;
          replaceRoute(router, newRoute, 'push');
          break;
        case 'search':
          Analytics.action('learningHubSearch', {
            searchQuery: data.data.query,
          });
          break;
        case 'onLoad':
          setIsLoading(false);
          break;
        default:
          assertNever(data);
      }
    };

    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
    };
  }, [router, baseRoute]);

  /**
   * Replace any helpjuice URLs with our own URLs. This way, a  right-click
   * open-in-new-tab will work as expected (instead of showing a 404 page).
   *
   * This will, for example, replace this:
   * https://watershed.helpjuice.com/carbon-removal-approaches
   *
   * ...with this:
   * https://dashboard.watershedclimate.com/learn#carbon-removal-approaches
   *
   * ...which means that when that URL ^ resolves, the customer is logged-in
   * to helpjuice correctly, rather than seeing an error page like they do
   * today.
   */
  useEffect(() => {
    if (!shouldModifyUrls) {
      return;
    }

    const updateLinksToWatershed = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as HTMLElement;
              const links = element.querySelectorAll('a');
              links.forEach((link) => {
                const href = link.getAttribute('href');
                const updatedHref = getWatershedLearnUrl(href, baseRoute);
                if (updatedHref) {
                  link.setAttribute('href', updatedHref);
                }
              });
            }
          });
        }
      });
    });

    const iframeDocument = iframeRef.current?.contentDocument;
    if (iframeDocument) {
      updateLinksToWatershed.observe(iframeDocument, {
        childList: true,
        subtree: true,
      });
    }

    return () => {
      updateLinksToWatershed.disconnect();
    };
  }, [baseRoute, shouldModifyUrls]);

  return (
    <>
      {/* This iframe loads learning hub using the encrypted URL in the background, to give the user the cookie to access the regular learning hub */}
      <iframe src={HELPJUICE_ENCRYPTED_URL} style={{ display: 'none' }} />
      {isLoading ? (
        <LoadingPage />
      ) : (
        <iframe
          ref={iframeRef}
          src={srcUrl}
          style={{
            border: 'none',
            width: '100%',
            height: '100%',
          }}
          onLoad={() => {
            // On load, send message to iframe allowing helpjuice to verify the origin of the iframe
            // Add 100ms timeout to ensure frame is ready to receive the message
            setTimeout(() => {
              iframeRef?.current?.contentWindow?.postMessage(
                'identify',
                'https://watershed.helpjuice.com'
              );
            }, 100);
          }}
        />
      )}
    </>
  );
}
